import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowLeft,
  lucideCheckCircle2,
  lucideCreditCard,
  lucideExternalLink,
  lucideHouse,
  lucideShield,
  lucideWifi,
  lucideWrench,
} from '@ng-icons/lucide';

import { ZardBadgeComponent } from '../../shared/components/badge';
import { ZardButtonComponent } from '../../shared/components/button';
import { ZardCardComponent } from '../../shared/components/card';

interface Partner {
  id: string;
  name: string;
  logo: string;
  description: string;
  features: string[];
  price?: string;
  discount?: string;
  url: string;
}

const PARTNERS_DATA: Record<string, Partner[]> = {
  comprehensive_insurance: [
    { id: 'axa', name: 'AXA', logo: '🛡️', description: 'Assurance tous risques complète pour propriétaires', features: ['Couverture dégâts des eaux', 'Protection incendie et vol', 'Responsabilité civile incluse', 'Assistance 24/7'], price: '25€/mois', discount: '-20% pour les membres BAILIA', url: 'https://www.axa.fr' },
    { id: 'allianz', name: 'Allianz', logo: '🛡️', description: 'Protection optimale pour votre patrimoine immobilier', features: ['Garantie tous risques', 'Protection juridique', 'Assistance en cas de sinistre', 'Indemnisation rapide'], price: '28€/mois', discount: '-15% pour les membres BAILIA', url: 'https://www.allianz.fr' },
    { id: 'maif', name: 'MAIF', logo: '🛡️', description: 'Assurance habitation propriétaire non occupant', features: ['Couverture complète', 'Garantie loyers impayés en option', 'Service client réactif', 'Gestion en ligne'], price: '22€/mois', discount: '-10% pour les membres BAILIA', url: 'https://www.maif.fr' },
  ],
  vacant_insurance: [
    { id: 'axa-pno', name: 'AXA PNO', logo: '🏠', description: 'Assurance propriétaire non occupant spécialisée', features: ['Protection logement vacant', 'Couverture vandalisme', 'Garantie dégradations', 'Tarifs préférentiels'], price: '18€/mois', discount: '-25% pour les membres BAILIA', url: 'https://www.axa.fr/assurance-pno' },
    { id: 'macif-pno', name: 'MACIF PNO', logo: '🏠', description: 'Protection adaptée aux biens non occupés', features: ['Assurance vacance locative', 'Garantie bris de glace', 'Protection juridique', 'Sans franchise'], price: '20€/mois', discount: '-15% pour les membres BAILIA', url: 'https://www.macif.fr' },
  ],
  internet_included: [
    { id: 'orange', name: 'Orange Pro', logo: '📡', description: 'Internet fibre pour logements locatifs', features: ["Fibre jusqu'à 1 Gb/s", 'Installation incluse', 'Box WiFi 6 incluse', 'Support technique dédié'], price: '35€/mois', discount: '-30% la première année', url: 'https://www.orange.fr' },
    { id: 'free-pro', name: 'Free Pro', logo: '📡', description: 'Connexion internet professionnelle', features: ['Débit garanti', 'IP fixe disponible', 'Sans engagement', 'Assistance 7j/7'], price: '29.99€/mois', discount: 'Première installation offerte', url: 'https://www.free.fr' },
  ],
  trusted_artisans: [
    { id: 'homeserve', name: 'HomeServe', logo: '🔧', description: "Réseau d'artisans qualifiés et vérifiés", features: ['Artisans certifiés RGE', 'Devis gratuits', 'Intervention sous 48h', 'Garantie décennale'], price: 'À partir de 50€', discount: '10% pour les membres BAILIA', url: 'https://www.homeserve.fr' },
    { id: 'hellocasa', name: 'HelloCasa', logo: '🔧', description: "Plateforme de mise en relation avec des artisans", features: ['Artisans sélectionnés', 'Avis clients vérifiés', 'Suivi de chantier', 'Paiement sécurisé'], price: 'Commission 8%', discount: 'Gratuit pour les membres BAILIA', url: 'https://www.hellocasa.fr' },
  ],
  rent_guarantee: [
    { id: 'gli-axa', name: 'AXA GLI', logo: '💰', description: 'Garantie loyers impayés complète', features: ["Couverture jusqu'à 24 mois", 'Protection juridique incluse', 'Frais de contentieux pris en charge', 'Indemnisation sous 30 jours'], price: '3.5% du loyer annuel', discount: '-20% pour les membres BAILIA', url: 'https://www.axa.fr/gli' },
    { id: 'gli-allianz', name: 'Allianz Loyers Garantis', logo: '💰', description: 'Protection contre les impayés de loyer', features: ['Prise en charge immédiate', 'Remboursement des loyers', 'Assistance juridique', 'Gestion des procédures'], price: '3.2% du loyer annuel', discount: '-15% pour les membres BAILIA', url: 'https://www.allianz.fr/gli' },
  ],
};

const QUESTION_ICONS: Record<string, string> = {
  comprehensive_insurance: 'lucideShield',
  vacant_insurance: 'lucideHouse',
  internet_included: 'lucideWifi',
  trusted_artisans: 'lucideWrench',
  rent_guarantee: 'lucideCreditCard',
};

@Component({
  selector: 'app-partners-page',
  imports: [
    NgIcon,
    ZardBadgeComponent,
    ZardButtonComponent,
    ZardCardComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    provideIcons({
      lucideArrowLeft,
      lucideExternalLink,
      lucideCheckCircle2,
      lucideShield,
      lucideWrench,
      lucideHouse,
      lucideWifi,
      lucideCreditCard,
    }),
  ],
  template: `
    <div class="min-h-screen bg-gray-50">
      <div class="max-w-4xl mx-auto min-h-screen flex flex-col">
        <div class="bg-[#1A2E47] px-4 pt-4 pb-6 shadow-md">
          <div class="flex items-center gap-3 mb-4">
            <button type="button" (click)="back.emit()" class="text-white hover:bg-white/10 p-1 rounded-full transition-colors">
              <ng-icon name="lucideArrowLeft" class="w-6 h-6" />
            </button>
            <div class="flex items-center gap-2">
              <img src="/logo-white.svg" alt="BAILIA" class="w-8 h-8" />
            </div>
          </div>
          <div class="flex items-center gap-3 mb-2">
            <div class="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/10 shadow-inner">
              <ng-icon [name]="iconName()" class="w-6 h-6 text-white" />
            </div>
            <div class="flex-1">
              <h1 class="text-xl text-white font-display font-bold">Nos partenaires</h1>
              <p class="text-sm text-white/70">{{ questionText() }}</p>
            </div>
          </div>
        </div>

        <div class="flex-1 px-4 py-6">
          <z-card class="p-4 bg-[#1A2E47]/5 border-[#1A2E47]/10 mb-6 border shadow-sm">
            <div class="flex items-start gap-3">
              <div class="w-10 h-10 bg-[#1A2E47] rounded-full flex items-center justify-center flex-shrink-0">
                <ng-icon name="lucideCheckCircle2" class="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 class="text-sm text-[#1A2E47] font-semibold mb-1">Pourquoi choisir un partenaire BAILIA ?</h3>
                <p class="text-xs text-[#1A2E47]/70 leading-relaxed">
                  Bénéficiez de tarifs préférentiels et d'un service de qualité avec nos partenaires sélectionnés. Tous nos partenaires sont vérifiés et de confiance.
                </p>
              </div>
            </div>
          </z-card>

          @if (partners().length > 0) {
            <div class="grid gap-4 mb-6">
              @for (partner of partners(); track partner.id) {
                <z-card class="overflow-hidden border-2 hover:border-[#00A878] transition-colors group">
                  <div class="p-6">
                    <div class="flex items-start justify-between mb-4">
                      <div class="flex items-center gap-3">
                        <div class="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center text-2xl border border-gray-100">{{ partner.logo }}</div>
                        <div>
                          <h3 class="text-lg font-bold text-[#1A2E47]">{{ partner.name }}</h3>
                          @if (partner.discount) {
                            <z-badge class="bg-[#00A878] hover:bg-[#008f66] mt-1 border-0">{{ partner.discount }}</z-badge>
                          }
                        </div>
                      </div>
                      @if (partner.price) {
                        <div class="text-right">
                          <p class="text-xl font-bold text-[#1A2E47]">{{ partner.price }}</p>
                        </div>
                      }
                    </div>
                    <p class="text-sm text-gray-600 mb-4 leading-relaxed">{{ partner.description }}</p>
                    <div class="space-y-2 mb-4">
                      @for (feature of partner.features; track feature) {
                        <div class="flex items-center gap-2">
                          <ng-icon name="lucideCheckCircle2" class="w-4 h-4 text-[#00A878] flex-shrink-0" />
                          <span class="text-sm text-gray-700">{{ feature }}</span>
                        </div>
                      }
                    </div>
                    <button z-button type="button" class="w-full bg-[#00A878] hover:bg-[#008f66] text-white shadow-sm" (click)="openUrl(partner.url)">
                      En savoir plus
                      <ng-icon name="lucideExternalLink" class="w-4 h-4 ml-2" />
                    </button>
                  </div>
                </z-card>
              }
            </div>
          } @else {
            <z-card class="p-8 text-center border-dashed border-2">
              <div class="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <ng-icon [name]="iconName()" class="w-8 h-8 text-gray-300" />
              </div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Partenaires à venir</h3>
              <p class="text-sm text-gray-500">Nous travaillons actuellement sur des partenariats pour cette catégorie.</p>
            </z-card>
          }

          <z-card class="p-4 bg-yellow-50 border-yellow-200">
            <p class="text-xs text-yellow-800">
              💡 <strong>Note :</strong> Vous n'êtes pas obligé de souscrire immédiatement. Vous pourrez revenir consulter nos partenaires à tout moment depuis votre profil.
            </p>
          </z-card>
        </div>

        <div class="px-4 pb-6 space-y-3">
          <button z-button type="button" class="w-full bg-[#00A878] hover:bg-[#008f66] h-12 text-white shadow-md font-medium text-base" (click)="continued.emit()">
            Continuer sans souscrire
          </button>
          <button z-button zType="outline" type="button" class="w-full h-12 border-2 border-[#1A2E47] text-[#1A2E47] hover:bg-gray-50 font-medium" (click)="back.emit()">
            Retour à la question
          </button>
        </div>
      </div>
    </div>
  `,
})
export class PartnersPageComponent {
  readonly questionId = input.required<string>();
  readonly questionText = input.required<string>();
  readonly back = output<void>();
  readonly continued = output<void>();

  protected readonly partners = computed<Partner[]>(() => PARTNERS_DATA[this.questionId()] || []);
  protected readonly iconName = computed(() => QUESTION_ICONS[this.questionId()] || 'lucideShield');

  protected openUrl(url: string): void {
    if (typeof window !== 'undefined') {
      window.open(url, '_blank');
    }
  }
}
